/**
 * Legal documents and card-brand badges for signed-in screens.
 *
 * `/terms` and `/privacy` have always rendered for a signed-in player, but the
 * only links to them sat on the register board and the landing footer — once in
 * the game there was no way back to the document. Navigating to the route would
 * also tear the game view down, so here the CMS page opens in a modal ON TOP of
 * whatever is open (settings, the checkout) and closing it returns the player to
 * exactly where they were.
 *
 * The same module supplies the two things the payment screens need before money
 * moves: a Terms consent row (the Pay / Request button stays disabled until it
 * is ticked) and the Visa / Mastercard badges.
 */
import { el, clear } from '../dom';
import { api } from '../../core/network/api';
import { legalDateLine, renderLegalMarkdown } from '../../core/legal/legal-screen';
import { fetchSiteInfo } from '../../core/preview/preview';

export type LegalSlug = 'terms' | 'privacy';

const FALLBACK_TITLE: Record<LegalSlug, string> = {
  terms: 'Terms of Service',
  privacy: 'Privacy Policy',
};

interface LegalPage {
  title: string;
  content: string;
  updatedAt?: string;
  effectiveDate?: string;
}

/**
 * Opens a CMS legal page in a modal above everything else (settings is z-60,
 * its full-screen sub-pages z-80). Returns the close function.
 */
export function openLegalDoc(slug: LegalSlug): () => void {
  const title = el('h2', 'legal-title pr-10', {}, FALLBACK_TITLE[slug]);
  const date = el('p', 'legal-updated');
  const body = el('div', 'legal-body', {}, el('p', '', {}, 'Loading…'));

  let layer: HTMLElement | null = null;
  const onKey = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    e.stopImmediatePropagation();
    close();
  };
  function close() {
    layer?.remove();
    layer = null;
    window.removeEventListener('keydown', onKey, true);
  }

  const sheet = el('div', 'legal-sheet relative w-full max-h-[90dvh] overflow-y-auto', {
    role: 'dialog', ariaModal: 'true', ariaLabel: FALLBACK_TITLE[slug],
  },
    el('button', 'tb-close-x absolute top-2 right-3 min-w-[44px] min-h-[44px] text-2xl leading-none text-[#4a2f16]',
      { type: 'button', ariaLabel: 'Close', onClick: close }, '×'),
    title, date, body);

  layer = el('div', 'fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-3',
    { onClick: (e) => { if (e.target === layer) close(); } }, sheet);
  document.body.appendChild(layer);
  // Window capture runs before the document-capture Escape handlers of the
  // settings panel and the checkout, so Escape closes only this sheet.
  window.addEventListener('keydown', onKey, true);

  api.get<LegalPage>(`/legal/${slug}`)
    .then((page) => {
      if (!layer) return;
      title.textContent = page.title || FALLBACK_TITLE[slug];
      date.textContent = legalDateLine(page);
      body.innerHTML = renderLegalMarkdown(page.content);
    })
    .catch(() => {
      if (!layer) return;
      clear(body);
      body.append(el('p', '', {}, 'This document could not be loaded. Please try again later or contact support.'));
    });

  return close;
}

/** An inline link-styled button that opens a legal document in the modal. */
export function legalLink(slug: LegalSlug, label: string = FALLBACK_TITLE[slug], cls = ''): HTMLButtonElement {
  return el('button', `underline underline-offset-2 hover:text-text ${cls}`, {
    type: 'button',
    onClick: (e: MouseEvent) => { e.preventDefault(); e.stopPropagation(); openLegalDoc(slug); },
  }, label);
}

/**
 * "I have read and accept the Terms of Service" — a checkbox row. `onChange`
 * fires with the new state; the caller keeps its submit button disabled until
 * it is true. The link opens the document without toggling the box.
 */
export function termsConsentRow(onChange: (accepted: boolean) => void): {
  row: HTMLElement;
  isAccepted: () => boolean;
} {
  const box = el('input', 'w-5 h-5 flex-none accent-[#e8c15a] cursor-pointer', {
    type: 'checkbox', name: 'accept-terms',
  });
  box.addEventListener('change', () => onChange(box.checked));
  const row = el('label', 'flex items-start gap-2.5 min-h-[44px] py-1 text-[12px] leading-snug text-text-dim cursor-pointer select-none', {},
    box,
    el('span', 'pt-0.5', {},
      'I have read and accept the ', legalLink('terms', 'Terms of Service', 'text-[#ffd97a]'),
      ' and the ', legalLink('privacy', 'Privacy Policy', 'text-[#ffd97a]'), '.'));
  return { row, isAccepted: () => box.checked };
}

/**
 * The card brands shown before a payment. Only Visa and Mastercard, and only the
 * ones the operator has switched ON in the admin's payment-methods list (the
 * same switches that drive the footer badges) — a brand switched off is simply
 * not shown, and with both off the row renders nothing at all.
 */
export const CARD_BRAND_SLUGS = ['visa', 'mastercard'] as const;

export function enabledCardBrands(
  methods: Array<{ slug: string; name: string; icon: string }> | undefined,
): Array<{ slug: string; name: string; icon: string }> {
  return (methods ?? []).filter((m) => (CARD_BRAND_SLUGS as readonly string[]).includes(m.slug));
}

/** Fills `host` with the enabled card badges; leaves it empty when none are on. */
export function mountCardBrands(host: HTMLElement): void {
  fetchSiteInfo()
    .then((si) => {
      const brands = enabledCardBrands(si.paymentMethods);
      clear(host);
      if (!brands.length) return;
      host.append(el('div', 'flex items-center justify-center gap-2', { dataset: { cardBrands: '' } },
        ...brands.map((b) => el('div',
          'flex items-center justify-center h-8 px-2.5 bg-white rounded border border-[#5a4632]/40',
          {},
          el('img', 'h-5 w-auto object-contain', {
            src: /^[a-z0-9_-]+$/i.test(b.icon) ? `/payment-methods/${b.icon}.svg` : '',
            alt: b.name, title: b.name,
          })))));
    })
    .catch(() => clear(host));
}
